// The sidebar entry for one selected feature (spec section 5): header with the whole
// id, type line, wkt, "also selected in", feature views, tag groups by prefix, the
// viewer's own properties, members, and the raw box. A `tbody`, so entries sit in the
// row's attributes table with its column widths.
import { NO_PREFIX_LABEL } from "./featureModel.mjs";
import {
  copyButton,
  el,
  renderPlainValue,
  renderProfile,
  renderRawBox,
  renderRawInline,
  renderSlotCells,
  renderSpotList,
  renderStepBlock,
  isCurvatureKey,
  valueText,
} from "./renderValues.mjs";
import { buildProfile } from "./positions.mjs";
import { isSlotKey } from "./slots.mjs";

// One line per tag: the key, then the value right-flush; the value moves under the
// key when both do not fit (style.css `.kv`).
function row(doc, label, value, className) {
  const tr = el(doc, "tr", className);
  const td = el(doc, "td", "fe-kvcell");
  td.colSpan = 2;
  const kv = el(doc, "div", "kv");
  const k = el(doc, "span", "k");
  k.append(...(Array.isArray(label) ? label : [label]));
  const v = el(doc, "span", "v");
  v.append(value);
  kv.append(k, v);
  td.append(kv);
  tr.append(td);
  return tr;
}

/** A key that wraps only after its `:`. */
function keySpan(doc, key) {
  const span = el(doc, "span", "fe-key");
  String(key)
    .split(/(?<=:)/)
    .forEach((part, i) => {
      if (i > 0) span.append(doc.createElement("wbr"));
      span.append(part);
    });
  return span;
}

function wideRow(doc, node, className) {
  const tr = el(doc, "tr", className);
  const td = el(doc, "td", "fe-wide");
  td.colSpan = 2;
  td.append(node);
  tr.append(td);
  return tr;
}

function groupHeader(doc, name, revisionText, className = "fe-group") {
  const tr = el(doc, "tr", className);
  tr.append(el(doc, "td", "fe-gname", name));
  tr.append(el(doc, "td", "fe-grev", revisionText));
  return tr;
}

function revisionText(group) {
  if (group.revision === undefined) return "";
  const layer =
    group.revisionLayer && group.revisionLayer !== group.prefix
      ? ` (layer ${group.revisionLayer})`
      : "";
  return `rev ${group.revision}${layer}`;
}

function countText(tag) {
  const n = tag.sections.length;
  if (tag.lr === "spot") return `${n} spot${n === 1 ? "" : "s"}`;
  if (tag.lr === "linear")
    return tag.sections.every((s) => s.endCm === null)
      ? `${n} points`
      : `${n} entries`;
  return `${n} section${n === 1 ? "" : "s"}`;
}

function lrBlock(doc, tag, lengthCm) {
  if (tag.lr === "spot") return renderSpotList(doc, tag);
  if (tag.lr === "linear") {
    const profile = buildProfile(tag.sections, lengthCm);
    if (profile.points.length > 0 || isCurvatureKey(tag.key))
      return renderProfile(doc, tag, lengthCm);
  }
  return renderStepBlock(doc, tag, lengthCm);
}

function tagRows(doc, tag, group, lengthCm) {
  const label = [keySpan(doc, tag.key)];
  if (tag.revision !== undefined && tag.revision !== group.revision)
    label.push(" ", el(doc, "span", "fe-rev", `rev ${tag.revision}`));
  if (tag.sections) {
    const keyRow = el(doc, "tr", "fe-lrkey");
    const td = el(doc, "td", "fe-keycell");
    td.colSpan = 2;
    td.append(...label, " ", el(doc, "span", "fe-count", countText(tag)), " ");
    td.append(renderRawInline(doc, tag));
    keyRow.append(td);
    const block = wideRow(doc, lrBlock(doc, tag, lengthCm), "fe-lrblock");
    block.dataset.rawKey = tag.rawKey;
    return [keyRow, block];
  }
  if (tag.lr) {
    // An LR key whose value does not parse: as tagged, whole.
    return [row(doc, label, el(doc, "span", "fe-value", valueText(tag.value)))];
  }
  if (isSlotKey(tag.key) && typeof tag.value === "string") {
    label.push(" ", renderRawInline(doc, tag));
    return [row(doc, label, renderSlotCells(doc, tag.value))];
  }
  return [row(doc, label, renderPlainValue(doc, tag.value))];
}

function lrText(lr) {
  if (!lr) return "";
  const ranges = lr.ranges ?? [lr];
  const text = ranges
    .map((r) =>
      r.endCm !== null
        ? `${r.startCm}–${r.endCm}`
        : lr.type === "spot"
          ? `${r.startCm}`
          : `${r.startCm}–end`,
    )
    .join(", ");
  return `${lr.type} ${text} cm`;
}

// Two lines per member: `# role · range`, then the whole id. Ids stay on one line and
// a sidebar is too narrow for them next to the other columns.
function membersTable(doc, members, missingMemberIds) {
  const table = el(doc, "table", "fe-members");
  members.forEach((m) => {
    const missing = missingMemberIds.has(m.id);
    const tr = el(doc, "tr", missing ? "fe-member fe-missing" : "fe-member");
    if (!missing) tr.dataset.member = m.id;
    tr.dataset.index = String(m.index);
    tr.append(el(doc, "td", "fe-num", m.index));
    const td = el(doc, "td");
    const head = el(doc, "div", undefined, m.roleName || "(no role)");
    const lr = lrText(m.lr);
    const note = [lr, missing ? "not loaded" : ""].filter(Boolean).join(" · ");
    if (note) head.append(" ", el(doc, "span", "fe-lr", note));
    const id = el(doc, "div", "fe-scroll");
    id.append(el(doc, "span", "fe-id", m.id), copyButton(doc, m.id));
    td.append(head, id);
    tr.append(td);
    table.append(tr);
  });
  return table;
}

/**
 * @param {Document} doc
 * @param {import("./featureModel.mjs").FeatureModel} model
 * @param {{headerText?: string, wkt: string|null, alsoIn: string[],
 *   views: Node[], viewTagKeys: Set<string>, missingMemberIds?: Set<string>}} options
 */
export function renderFeatureEntry(doc, model, options) {
  const {
    headerText,
    wkt,
    alsoIn = [],
    views = [],
    viewTagKeys = new Set(),
    missingMemberIds = new Set(),
  } = options;
  const root = el(doc, "tbody", "fe-entry");
  root.dataset.featureId = model.id;

  const head = el(doc, "tr", "fe-head");
  const th = el(doc, "th");
  th.colSpan = 2;
  const title = headerText ?? model.id;
  th.append(el(doc, "span", "fe-id", title), copyButton(doc, title));
  head.append(th);
  root.append(head);

  const typeParts = [
    model.type,
    model.geometryType,
    model.lengthCm !== null ? `${model.lengthCm} cm` : undefined,
  ].filter(Boolean);
  if (typeParts.length)
    root.append(
      row(doc, "type", el(doc, "span", undefined, typeParts.join(" · "))),
    );
  if (wkt) {
    const input = el(doc, "input");
    input.disabled = true;
    input.value = wkt;
    root.append(row(doc, "wkt", input, "fe-wktrow"));
  }
  // Always present, hidden when empty: the glue refreshes it as other rows select.
  const also = row(
    doc,
    "also selected in",
    el(doc, "span", "fe-alsorows", alsoIn.join(", ")),
    "fe-also",
  );
  if (!alsoIn.length) also.style.display = "none";
  root.append(also);
  views.forEach((view) => root.append(wideRow(doc, view, "fe-viewrow")));

  model.groups.forEach((group) => {
    root.append(
      groupHeader(doc, group.prefix || NO_PREFIX_LABEL, revisionText(group)),
    );
    group.tags
      .filter((t) => !viewTagKeys.has(t.rawKey))
      .forEach((tag) =>
        root.append(...tagRows(doc, tag, group, model.lengthCm)),
      );
  });

  if (model.viewer.length) {
    root.append(groupHeader(doc, "viewer", ""));
    model.viewer.forEach((tag) =>
      root.append(
        row(doc, keySpan(doc, tag.key), renderPlainValue(doc, tag.value)),
      ),
    );
  }

  if (model.members.length) {
    root.append(
      groupHeader(doc, `members (${model.members.length})`, "", "fe-mhead"),
    );
    root.append(
      wideRow(doc, membersTable(doc, model.members, missingMemberIds)),
    );
  }

  root.append(wideRow(doc, renderRawBox(doc, model.properties), "fe-rawrow"));
  return root;
}
